import * as React from 'react';
import { useState } from 'react';
import Box from '@mui/material/Box';
import { Grid, useTheme, IconButton } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ItemFormModal from '../../components/form/ItemFormModal';
import { formatXmlUnitPrice } from '../../utils/currencyFormat';
import { lineItemCalculation, resolvePriceBaseAmount } from '../../utils/pricingEngineForDocuments';
import { useTranslatedMessages } from '../../hooks/useTranslatedMessages';
import { getArrayItemFieldErrors } from '../../utils/apiFieldErrors';

const ItemDetailsTable = ({
  items = [],
  remove,
  replace,
  readOnly,
  invoiceTypeCode,
  profileExecutionId,
  errors,
  restrictToRcm = false,
}) => {
  const theme = useTheme();
  const { eInvoiceForm } = useTranslatedMessages();
  const { item_details_table: t } = eInvoiceForm;
  const col = t.columns;
  const actions = t.actions;
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const handleEditClick = (item) => {
    const rowIndex = items.findIndex((lineItem) => lineItem.key === item.key);
    handleOpen();
    setCurrentItem(item);
    setCurrentItemIndex(rowIndex);
  };

  const handleViewClick = (item) => {
    const rowIndex = items.findIndex((lineItem) => lineItem.key === item.key);
    handleOpen();
    setCurrentItem(item);
    setCurrentItemIndex(rowIndex);
  };

  const handleDeleteClick = (params) => {
    const rowIndex = items.findIndex((item) => item.key === params.row.key);
    remove(rowIndex);
  };

  const replaceItem = (item) => {
    const updatedItems = items.map((lineItem) => (lineItem.key === item.key ? { ...lineItem, ...item } : lineItem));

    replace(updatedItems);
    handleClose();
    setCurrentItemIndex(null);
  };

  const getRowErrorMessages = (rowIndex) => {
    if (rowIndex < 0) {
      return [];
    }

    const fieldErrors = getArrayItemFieldErrors(errors, 'document_lines', rowIndex);

    return Object.values(fieldErrors);
  };

  const hasRowErrors = (rowIndex) => getRowErrorMessages(rowIndex).length > 0;

  const columns = [
    {
      headerName: col.item_name,
      field: 'name',
      flex: 1,
    },
    {
      field: 'quantity',
      headerName: col.quantity,
      flex: 1,
    },
    {
      field: 'price',
      headerName: col.unit_price,
      flex: 1,
      renderCell: (params) => {
        const baseAmount = resolvePriceBaseAmount(params.value);
        const allowanceAmount = params.value?.allowance?.amount ?? 0;
        const priceAmount = baseAmount - allowanceAmount;
        return formatXmlUnitPrice(priceAmount);
      },
    },
    {
      field: 'allowance_amount',
      headerName: col.allowance,
      flex: 1,
      renderCell: (params) => {
        const { allowance_amount } = lineItemCalculation(params?.row);
        return formatXmlUnitPrice(allowance_amount || 0);
      },
    },
    {
      field: 'charge_amount',
      headerName: col.charge,
      flex: 1,
      renderCell: (params) => {
        const { charge_amount } = lineItemCalculation(params?.row);
        return formatXmlUnitPrice(charge_amount || 0);
      },
    },
    {
      field: 'net_amount',
      headerName: col.total_before_tax,
      flex: 1,
      valueGetter: (params) => formatXmlUnitPrice(params),
    },
    {
      field: 'tax_amount',
      headerName: col.tax,
      flex: 1,
      renderCell: (params) => {
        const tax = params.row;
        const rate = tax.classified_tax_category?.percent ?? 0;
        const vatCategoryCode = tax.classified_tax_category?.id;
        const { tax_amount } = lineItemCalculation(params.row);
        return `${formatXmlUnitPrice(tax_amount)}  (${vatCategoryCode}-${Number(rate).toFixed(2)}%)`;
      },
    },
    {
      field: 'total_amount',
      headerName: col.total_amount,
      flex: 1,
      valueGetter: (params) => formatXmlUnitPrice(params),
    },
  ];

  const commonActionColumn = {
    field: 'actions',
    headerName: col.actions,
    headerClassName: 'sticky-header-last',
    cellClassName: 'sticky-header-last',
    width: 80,
    sortable: false,
    filterable: false,
  };

  if (readOnly) {
    columns.push({
      ...commonActionColumn,
      renderCell: (params) => (
        <IconButton
          onClick={() => handleViewClick(params.row)}
          size="small"
          data-testid={`view-item-${params.id}`}
          sx={{ color: theme.palette.primary.main }}
        >
          <VisibilityIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ),
    });
  } else {
    columns.push({
      ...commonActionColumn,
      type: 'actions',
      getActions: (params) => {
        const rowIndex = items.findIndex((item) => item.key === params.row.key);
        const rowHasErrors = hasRowErrors(rowIndex);

        return [
          ...(rowHasErrors
            ? [
                <GridActionsCellItem
                  key={`error-${params.id}`}
                  icon={<ErrorOutlineIcon color="error" fontSize="small" />}
                  label={getRowErrorMessages(rowIndex).join('; ')}
                  onClick={() => handleEditClick(params.row)}
                  showInMenu={false}
                />,
              ]
            : []),
          <GridActionsCellItem
            key={`edit-${params.id}`}
            label={actions.edit}
            onClick={() => handleEditClick(params.row)}
            showInMenu
            data-testid={`edit-item-${params.id}`}
          />,
          <GridActionsCellItem
            key={`remove-${params.id}`}
            label={actions.remove}
            onClick={() => {
              handleDeleteClick(params);
            }}
            showInMenu
            data-testid={`remove-item-${params.id}`}
          />,
        ];
      },
    });
  }

  return (
    <Grid container>
      <Grid size={12}>
        <Box
          sx={{
            mt: 1,
            width: '100%',
            overflow: 'auto',
            display: 'grid',
          }}
        >
          <DataGrid
            rows={items ?? []}
            getRowId={(row) => row.id || row._id}
            getRowClassName={(params) => {
              const rowIndex = items.findIndex((item) => item.key === params.row.key);

              return hasRowErrors(rowIndex) ? 'line-item-row-error' : '';
            }}
            columns={columns}
            rowCount={items.length}
            paginationMode="server"
            hideFooter
            style={{ width: '100%' }}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 100,
                },
              },
            }}
            pageSizeOptions={[10, 25]}
            disableColumnMenu
            disableColumnSorting
            disableVirtualization
            disableRowSelectionOnClick
            sx={{
              overflow: 'auto',
              '& .line-item-row-error': {
                backgroundColor: (theme) => `${theme.palette.error.main}14`,
              },
              '& .sticky-header-last, & .sticky-cell-last': {
                position: 'sticky',
                right: -1,
                zIndex: 4,
                backgroundColor: '#fff',
                '& .MuiButtonBase-root': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                },
              },
            }}
          />
        </Box>
      </Grid>
      <ItemFormModal
        open={open}
        handleClose={handleClose}
        handleSubmit={replaceItem}
        initialData={currentItem}
        invoiceTypeCode={invoiceTypeCode}
        profileExecutionId={profileExecutionId}
        restrictToRcm={restrictToRcm}
        externalFieldErrors={
          currentItemIndex != null ? getArrayItemFieldErrors(errors, 'document_lines', currentItemIndex) : {}
        }
        edit
        readOnly={readOnly}
      />
    </Grid>
  );
};

export default ItemDetailsTable;
